import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, XCircle, BarChart3, Activity, DollarSign, Shield, Info } from 'lucide-react';

const WORKER_URL = 'https://tickertapapi.avetter90.workers.dev';

// ── Formatters ──────────────────────────────────────────────────────────────
const fmtPct   = (v, digits = 1) => v != null ? `${(v * 100).toFixed(digits)}%` : '—';
const fmtPctRaw = (v, digits = 1) => v != null ? `${v.toFixed(digits)}%` : '—';
const fmtNum   = (v, digits = 2) => v != null ? v.toFixed(digits) : '—';
const fmtBig   = (v) => {
  if (v == null) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9)  return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6)  return `$${(v / 1e6).toFixed(1)}M`;
  return `$${v.toLocaleString()}`;
};
const fmtVol   = (v) => {
  if (v == null) return '—';
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return `${v}`;
};
const fmtFCF   = (v) => {
  if (v == null) return '—';
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(0)}M`;
  return `${sign}$${abs.toLocaleString()}`;
};

// ── Tooltip definitions ──────────────────────────────────────────────────────
const TOOLTIPS = {
  'Yield':        'Annual dividend / current price. Core income metric.',
  'Payout':       'Dividends paid as % of earnings. >90% = sustainability risk.',
  'Coverage':     'FCF per share ÷ dividend per share. >1.5x = safe.',
  'P/E':          'Price ÷ earnings. Lower = cheaper relative to earnings.',
  'Exp Ratio':    'Annual fund management fee. Lower is better for net return.',
  'AUM':          'Total assets under management. Larger = more liquid, lower closure risk.',
  'Beta':         'Price volatility vs market. <1 = less volatile, >1 = more volatile.',
  'Volume':       'Average daily shares traded. Higher = easier to enter/exit.',
  'Short %':      'Shares sold short as % of float. >20% = elevated bearish pressure.',
  'Mkt Cap':      'Total market value. Proxy for size and stability.',
  '3M':           'Price return over last 3 months. Recent momentum signal.',
  '6M':           'Price return over last 6 months. Medium-term trend.',
  '12M':          'Price return over last 12 months. Full-year performance.',
  'Rev Growth':   'Revenue growth year-over-year. Business expansion signal.',
  'Net Income':   'Net income growth year-over-year. Earnings trend.',
  'Free Cash Flow': 'Cash generated after capex. Funds dividend payments.',
};

// ── Tooltip component ────────────────────────────────────────────────────────
function Tip({ label }) {
  const [show, setShow] = useState(false);
  const text = TOOLTIPS[label];
  if (!text) return null;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 3 }}>
      <Info
        size={10}
        color="#333"
        style={{ cursor: 'help', flexShrink: 0 }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span style={{
          position: 'absolute', bottom: '130%', left: '50%', transform: 'translateX(-50%)',
          background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 6,
          padding: '5px 9px', fontSize: 10, color: '#aaa', whiteSpace: 'nowrap',
          zIndex: 100, pointerEvents: 'none', maxWidth: 200, lineHeight: 1.4,
          whiteSpace: 'normal', width: 180, textAlign: 'center'
        }}>
          {text}
        </span>
      )}
    </span>
  );
}

// ── Score badge ──────────────────────────────────────────────────────────────
function ScoreBadge({ score }) {
  if (score == null) return null;
  let label, color, bg;
  if (score >= 70)      { label = 'STRONG';  color = '#00d4aa'; bg = 'rgba(0,212,170,0.12)'; }
  else if (score >= 50) { label = 'SOLID';   color = '#22c55e'; bg = 'rgba(34,197,94,0.12)'; }
  else if (score >= 35) { label = 'FAIR';    color = '#f59e0b'; bg = 'rgba(245,158,11,0.12)'; }
  else                  { label = 'WEAK';    color = '#ef4444'; bg = 'rgba(239,68,68,0.12)'; }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 22, fontWeight: 700, color }}>{score}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color, background: bg, border: `1px solid ${color}40`, padding: '2px 7px', borderRadius: 4, letterSpacing: 1 }}>{label}</span>
    </div>
  );
}

// ── Trend indicator ──────────────────────────────────────────────────────────
function Trend({ value, good = 'up' }) {
  if (value == null) return <span style={{ color: '#444' }}>—</span>;
  const isPositive = value > 0;
  const isGood = good === 'up' ? isPositive : !isPositive;
  const color = isGood ? '#22c55e' : '#ef4444';
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const sign = isPositive ? '+' : '';
  return (
    <span style={{ color, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <Icon size={12} />
      {sign}{fmtPct(value)}
    </span>
  );
}

// ── FCF pill ─────────────────────────────────────────────────────────────────
function FCFPill({ positive, value }) {
  if (positive == null) return <span style={{ color: '#444' }}>—</span>;
  const color = positive ? '#22c55e' : '#ef4444';
  const bg    = positive ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)';
  return (
    <span style={{ color, background: bg, border: `1px solid ${color}30`, padding: '1px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
      {positive ? '✓' : '✗'} {fmtFCF(value)}
    </span>
  );
}

// ── Metric cell (with optional tooltip) ──────────────────────────────────────
function MetricCell({ label, value, sub }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#555', marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        {label}<Tip label={label} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#e0e0e0' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#444', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

// ── Price momentum row ────────────────────────────────────────────────────────
function MomentumRow({ p3m, p6m, p12m }) {
  if (p3m == null && p6m == null && p12m == null) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>3M<Tip label="3M" /></div>
        <Trend value={p3m} good="up" />
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>6M<Tip label="6M" /></div>
        <Trend value={p6m} good="up" />
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>12M<Tip label="12M" /></div>
        <Trend value={p12m} good="up" />
      </div>
    </div>
  );
}

// ── Payout health color ──────────────────────────────────────────────────────
function payoutColor(payout) {
  if (payout == null) return '#888';
  if (payout < 0) return '#f97316';
  if (payout > 1.0) return '#ef4444';
  if (payout > 0.90) return '#f97316';
  if (payout > 0.75) return '#f59e0b';
  return '#22c55e';
}

// ── Compare Modal ────────────────────────────────────────────────────────────
function CompareModal({ seed, result, onClose }) {
  const rows = result.isEtf
    ? [
        { label: 'Yield',         a: fmtPct(seed.dividendYield),   b: fmtPct(result.dividendYield) },
        { label: 'Expense Ratio', a: fmtPct(seed.expenseRatio, 2), b: fmtPct(result.expenseRatio, 2) },
        { label: 'AUM',           a: fmtBig(seed.aum),             b: fmtBig(result.aum) },
        { label: 'Beta',          a: fmtNum(seed.beta),            b: fmtNum(result.beta) },
        { label: 'Short %',       a: fmtPctRaw(seed.shortPct),     b: fmtPctRaw(result.shortPct) },
        { label: '3M',            a: fmtPct(seed.priceChange3m),   b: fmtPct(result.priceChange3m) },
        { label: '6M',            a: fmtPct(seed.priceChange6m),   b: fmtPct(result.priceChange6m) },
        { label: '12M',           a: fmtPct(seed.priceChange12m),  b: fmtPct(result.priceChange12m) },
        { label: 'Volume',        a: fmtVol(seed.volume),          b: fmtVol(result.volume) },
        { label: 'Market Cap',    a: fmtBig(seed.marketCap),       b: fmtBig(result.marketCap) },
        { label: 'Score',         a: seed.qualityScore ?? '—',     b: result.qualityScore },
      ]
    : [
        { label: 'Yield',         a: fmtPct(seed.dividendYield),   b: fmtPct(result.dividendYield) },
        { label: 'Payout Ratio',  a: fmtPct(seed.payoutRatio),     b: fmtPct(result.payoutRatio) },
        { label: 'Coverage',      a: seed.coverageRatio != null ? `${seed.coverageRatio}x` : '—', b: result.coverageRatio != null ? `${result.coverageRatio}x` : '—' },
        { label: 'P/E',           a: fmtNum(seed.peRatio, 1),      b: fmtNum(result.peRatio, 1) },
        { label: 'Rev Growth',    a: fmtPct(seed.revenueGrowth),   b: fmtPct(result.revenueGrowth) },
        { label: 'NI Growth',     a: fmtPct(seed.netIncomeGrowth), b: fmtPct(result.netIncomeGrowth) },
        { label: 'FCF',           a: fmtFCF(seed.fcf),             b: fmtFCF(result.fcf) },
        { label: '3M',            a: fmtPct(seed.priceChange3m),   b: fmtPct(result.priceChange3m) },
        { label: '6M',            a: fmtPct(seed.priceChange6m),   b: fmtPct(result.priceChange6m) },
        { label: '12M',           a: fmtPct(seed.priceChange12m),  b: fmtPct(result.priceChange12m) },
        { label: 'Beta',          a: fmtNum(seed.beta),            b: fmtNum(result.beta) },
        { label: 'Short %',       a: fmtPctRaw(seed.shortPct),     b: fmtPctRaw(result.shortPct) },
        { label: 'Volume',        a: fmtVol(seed.volume),          b: fmtVol(result.volume) },
        { label: 'Market Cap',    a: fmtBig(seed.marketCap),       b: fmtBig(result.marketCap) },
        { label: 'Score',         a: seed.qualityScore ?? '—',     b: result.qualityScore },
      ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
         onClick={onClose}>
      <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: 12, width: '100%', maxWidth: 560, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Side-by-side comparison</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #1a1a1a' }}>
          <div style={{ background: '#0a0a0a', padding: '10px 14px', borderBottom: '1px solid #1a1a1a' }} />
          <div style={{ background: '#0f1520', padding: '10px 14px', textAlign: 'center', borderBottom: '1px solid #1a1a1a', borderLeft: '1px solid #1a1a1a' }}>
            <div style={{ color: '#00d4aa', fontWeight: 700, fontSize: 13 }}>{seed.ticker}</div>
            <div style={{ color: '#555', fontSize: 10 }}>YOUR HOLDING</div>
          </div>
          <div style={{ background: '#0f0f0f', padding: '10px 14px', textAlign: 'center', borderBottom: '1px solid #1a1a1a', borderLeft: '1px solid #1a1a1a' }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{result.symbol}</div>
            <div style={{ color: '#555', fontSize: 10 }}>ALTERNATIVE</div>
          </div>
          {rows.map((row, i) => (
            <React.Fragment key={row.label}>
              <div style={{ background: '#0a0a0a', padding: '9px 14px', borderBottom: i < rows.length - 1 ? '1px solid #111' : 'none', color: '#555', fontSize: 11 }}>{row.label}</div>
              <div style={{ background: '#0f1520', padding: '9px 14px', borderBottom: i < rows.length - 1 ? '1px solid #111' : 'none', textAlign: 'center', borderLeft: '1px solid #1a1a1a', color: '#e0e0e0', fontSize: 13, fontWeight: 500 }}>{row.a}</div>
              <div style={{ background: '#0f0f0f', padding: '9px 14px', borderBottom: i < rows.length - 1 ? '1px solid #111' : 'none', textAlign: 'center', borderLeft: '1px solid #1a1a1a', color: '#e0e0e0', fontSize: 13, fontWeight: 500 }}>{row.b}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Result Card ──────────────────────────────────────────────────────────────
function ResultCard({ result, seed, rank }) {
  const [expanded, setExpanded] = useState(false);
  const [comparing, setComparing] = useState(false);

  const isEtf = result.isEtf;
  const yieldDelta = result.yieldDelta;

  return (
    <>
      {comparing && <CompareModal seed={seed} result={result} onClose={() => setComparing(false)} />}
      <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.2s' }}
           onMouseEnter={e => e.currentTarget.style.borderColor = '#2a2a2a'}
           onMouseLeave={e => e.currentTarget.style.borderColor = '#1a1a1a'}>

        {/* Card Header */}
        <div style={{ padding: '16px 18px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ color: '#00d4aa', fontWeight: 700, fontSize: 15 }}>{result.symbol}</span>
                <span style={{ fontSize: 10, color: '#555', background: '#1a1a1a', padding: '1px 7px', borderRadius: 3, letterSpacing: 0.5 }}>{isEtf ? 'ETF' : 'STOCK'}</span>
                {result.assetClass && (
                  <span style={{ fontSize: 10, color: '#444', background: '#111', padding: '1px 7px', borderRadius: 3 }}>{result.assetClass}</span>
                )}
                {yieldDelta != null && (
                  <span style={{ fontSize: 10, color: yieldDelta >= 0 ? '#22c55e' : '#f59e0b', background: yieldDelta >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)', padding: '1px 7px', borderRadius: 3 }}>
                    {yieldDelta >= 0 ? '+' : ''}{fmtPct(yieldDelta)} vs yours
                  </span>
                )}
              </div>
              <div style={{ color: '#888', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{result.name}</div>
              <div style={{ color: '#444', fontSize: 11, marginTop: 2 }}>{result.sector}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
              <ScoreBadge score={result.qualityScore} />
              {result.price != null && <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>${result.price?.toFixed(2)}</div>}
            </div>
          </div>
        </div>

        {/* ── Section 1: Income ── */}
        <div style={{ padding: '10px 18px', borderTop: '1px solid #161616', background: '#0b0b0b' }}>
          <div style={{ fontSize: 9, color: '#444', letterSpacing: 1.5, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Income</div>
          <div style={{ display: 'grid', gridTemplateColumns: isEtf ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)', gap: 4 }}>
            <MetricCell label="Yield" value={<span style={{ color: '#00d4aa', fontWeight: 700 }}>{fmtPct(result.dividendYield)}</span>} />
            {isEtf ? (
              <>
                <MetricCell label="Exp Ratio" value={<span style={{ color: result.expenseRatio != null && result.expenseRatio > 0.005 ? '#f59e0b' : '#22c55e' }}>{fmtPct(result.expenseRatio, 2)}</span>} />
                <MetricCell label="AUM" value={fmtBig(result.aum)} />
              </>
            ) : (
              <>
                <MetricCell label="Payout" value={<span style={{ color: payoutColor(result.payoutRatio), fontWeight: 600 }}>{fmtPct(result.payoutRatio)}</span>} />
                <MetricCell label="Coverage" value={result.coverageRatio != null ? `${result.coverageRatio}x` : '—'} />
                <MetricCell label="P/E" value={fmtNum(result.peRatio, 1)} />
              </>
            )}
          </div>
        </div>

        {/* ── Section 2: Price Momentum ── */}
        {(result.priceChange3m != null || result.priceChange6m != null || result.priceChange12m != null) && (
          <div style={{ padding: '10px 18px', borderTop: '1px solid #161616', background: '#0a0a0a' }}>
            <div style={{ fontSize: 9, color: '#444', letterSpacing: 1.5, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Momentum</div>
            <MomentumRow p3m={result.priceChange3m} p6m={result.priceChange6m} p12m={result.priceChange12m} />
          </div>
        )}

        {/* ── Section 3: Business Health (stocks only, expandable) ── */}
        {!isEtf && (
          <div style={{ borderTop: '1px solid #161616' }}>
            <button
              onClick={() => setExpanded(v => !v)}
              style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 9, color: expanded ? '#00d4aa' : '#444', letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase' }}>Business Health</span>
              {expanded ? <ChevronUp size={13} color="#444" /> : <ChevronDown size={13} color="#333" />}
            </button>
            {expanded && (
              <div style={{ padding: '2px 18px 14px', background: '#0b0b0b' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>Rev Growth<Tip label="Rev Growth" /></div>
                    <Trend value={result.revenueGrowth} good="up" />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>Net Income<Tip label="Net Income" /></div>
                    <Trend value={result.netIncomeGrowth} good="up" />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>Free Cash Flow<Tip label="Free Cash Flow" /></div>
                    <FCFPill positive={result.fcfPositive} value={result.fcf} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ETF fund details expansion ── */}
        {isEtf && (
          <div style={{ borderTop: '1px solid #161616' }}>
            <button
              onClick={() => setExpanded(v => !v)}
              style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 9, color: expanded ? '#00d4aa' : '#444', letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase' }}>Fund Details</span>
              {expanded ? <ChevronUp size={13} color="#444" /> : <ChevronDown size={13} color="#333" />}
            </button>
            {expanded && (
              <div style={{ padding: '2px 18px 14px', background: '#0b0b0b' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  <MetricCell label="Asset Class" value={<span style={{ fontSize: 11 }}>{result.assetClass || result.sector || '—'}</span>} />
                  <MetricCell label="Mkt Cap" value={fmtBig(result.marketCap)} />
                  <MetricCell label="Exchange" value={<span style={{ fontSize: 11 }}>{result.exchange || '—'}</span>} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Section 4: Market ── */}
        <div style={{ padding: '10px 18px', borderTop: '1px solid #161616', background: '#0a0a0a' }}>
          <div style={{ fontSize: 9, color: '#444', letterSpacing: 1.5, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Market</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
            <MetricCell label="Beta" value={fmtNum(result.beta)} />
            <MetricCell label="Volume" value={fmtVol(result.volume)} />
            <MetricCell label="Short %" value={
              result.shortPct != null
                ? <span style={{ color: result.shortPct > 20 ? '#ef4444' : result.shortPct > 10 ? '#f59e0b' : '#888' }}>{fmtPctRaw(result.shortPct)}</span>
                : '—'
            } />
            <MetricCell label="Mkt Cap" value={fmtBig(result.marketCap)} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #161616', padding: '9px 18px', textAlign: 'center' }}>
          <button onClick={() => setComparing(true)}
            style={{ background: 'none', border: 'none', color: '#444', fontSize: 12, cursor: 'pointer', letterSpacing: 0.3 }}
            onMouseEnter={e => e.target.style.color = '#00d4aa'}
            onMouseLeave={e => e.target.style.color = '#444'}>
            Side-by-side comparison →
          </button>
        </div>
      </div>
    </>
  );
}

// ── Seed Card (Your Holding) ─────────────────────────────────────────────────
function SeedCard({ seed }) {
  const [expanded, setExpanded] = useState(false);
  const isEtf = seed.isEtf;

  return (
    <div style={{ background: '#0d1520', border: '1px solid #00d4aa30', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ color: '#00d4aa', fontWeight: 700, fontSize: 16 }}>{seed.ticker}</span>
            <span style={{ fontSize: 10, color: '#00d4aa', background: 'rgba(0,212,170,0.12)', border: '1px solid #00d4aa40', padding: '1px 8px', borderRadius: 3, letterSpacing: 0.5 }}>YOUR HOLDING</span>
            <span style={{ fontSize: 10, color: '#555', background: '#1a1a1a', padding: '1px 7px', borderRadius: 3 }}>{isEtf ? 'ETF' : 'STOCK'}</span>
          </div>
          <div style={{ color: '#888', fontSize: 12 }}>{seed.name}</div>
          <div style={{ color: '#444', fontSize: 11, marginTop: 2 }}>{seed.sector}</div>
        </div>
        {seed.price != null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#e0e0e0', fontWeight: 600, fontSize: 16 }}>${seed.price?.toFixed(2)}</div>
            <div style={{ color: '#555', fontSize: 11, marginTop: 2 }}>{fmtBig(seed.marketCap)}</div>
          </div>
        )}
      </div>

      {/* Income row */}
      <div style={{ padding: '10px 20px', borderTop: '1px solid #00d4aa15', background: '#0a1018' }}>
        <div style={{ fontSize: 9, color: '#00d4aa80', letterSpacing: 1.5, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Income</div>
        <div style={{ display: 'grid', gridTemplateColumns: isEtf ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)', gap: 4 }}>
          <MetricCell label="Yield" value={<span style={{ color: '#00d4aa', fontWeight: 700 }}>{fmtPct(seed.dividendYield)}</span>} />
          {isEtf ? (
            <>
              <MetricCell label="Exp Ratio" value={fmtPct(seed.expenseRatio, 2)} />
              <MetricCell label="AUM" value={fmtBig(seed.aum)} />
            </>
          ) : (
            <>
              <MetricCell label="Payout" value={<span style={{ color: payoutColor(seed.payoutRatio) }}>{fmtPct(seed.payoutRatio)}</span>} />
              <MetricCell label="Coverage" value={seed.coverageRatio != null ? `${seed.coverageRatio}x` : '—'} />
              <MetricCell label="P/E" value={fmtNum(seed.peRatio, 1)} />
            </>
          )}
        </div>
      </div>

      {/* Momentum row */}
      {(seed.priceChange3m != null || seed.priceChange6m != null || seed.priceChange12m != null) && (
        <div style={{ padding: '10px 20px', borderTop: '1px solid #00d4aa15', background: '#091018' }}>
          <div style={{ fontSize: 9, color: '#00d4aa80', letterSpacing: 1.5, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Momentum</div>
          <MomentumRow p3m={seed.priceChange3m} p6m={seed.priceChange6m} p12m={seed.priceChange12m} />
        </div>
      )}

      {/* Business Health expandable (stocks only) */}
      {!isEtf && (
        <div style={{ borderTop: '1px solid #00d4aa15' }}>
          <button onClick={() => setExpanded(v => !v)}
            style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: expanded ? '#00d4aa' : '#00d4aa60', letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase' }}>Business Health</span>
            {expanded ? <ChevronUp size={13} color="#00d4aa60" /> : <ChevronDown size={13} color="#00d4aa40" />}
          </button>
          {expanded && (
            <div style={{ padding: '2px 20px 14px', background: '#0a1018' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Revenue Growth</div>
                  <Trend value={seed.revenueGrowth} good="up" />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Net Income</div>
                  <Trend value={seed.netIncomeGrowth} good="up" />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Free Cash Flow</div>
                  <FCFPill positive={seed.fcfPositive} value={seed.fcf} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Market row */}
      <div style={{ padding: '10px 20px', borderTop: '1px solid #00d4aa15', background: '#091018' }}>
        <div style={{ fontSize: 9, color: '#00d4aa80', letterSpacing: 1.5, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Market</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
          <MetricCell label="Beta" value={fmtNum(seed.beta)} />
          <MetricCell label="Volume" value={fmtVol(seed.volume)} />
          <MetricCell label="Short %" value={seed.shortPct != null ? fmtPctRaw(seed.shortPct) : '—'} />
          <MetricCell label="Exchange" value={<span style={{ fontSize: 11 }}>{seed.exchange || '—'}</span>} />
        </div>
      </div>
    </div>
  );
}

// ── Asset Class Filter Tabs ───────────────────────────────────────────────────
const ASSET_CLASS_TABS = [
  { value: 'all',          label: 'All' },
  { value: 'equity',       label: 'Equity ETFs' },
  { value: 'fixed_income', label: 'Fixed Income' },
  { value: 'stocks_only',  label: 'Stocks Only' },
  { value: 'etfs_only',    label: 'ETFs Only' },
];

function AssetClassTabs({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
      {ASSET_CLASS_TABS.map(tab => (
        <button key={tab.value} onClick={() => onChange(tab.value)}
          style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
            background: value === tab.value ? 'rgba(0,212,170,0.12)' : '#0f0f0f',
            color: value === tab.value ? '#00d4aa' : '#555',
            border: `1px solid ${value === tab.value ? '#00d4aa33' : '#1a1a1a'}`,
            transition: 'all 0.15s'
          }}>
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ── Mode B Filter Bar ─────────────────────────────────────────────────────────
function FilterBar({ filters, onChange, onRun, loading }) {
  const sectors = ['', 'Technology', 'Healthcare', 'Financial Services', 'Consumer Defensive', 'Utilities', 'Energy', 'Real Estate', 'Industrials', 'Basic Materials', 'Communication Services', 'Consumer Cyclical'];

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: 10, padding: '16px 18px', marginBottom: 20 }}>
      <div style={{ fontSize: 9, color: '#555', letterSpacing: 1.5, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase' }}>Screen Universe Filters</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
        {/* Yield Min */}
        <div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 5 }}>Yield Min</div>
          <input type="number" step="0.5" min="0" max="30" value={(filters.yieldMin * 100).toFixed(1)}
            onChange={e => onChange({ ...filters, yieldMin: parseFloat(e.target.value) / 100 || 0 })}
            style={{ width: '100%', background: '#1a1a1a', border: '1px solid #222', borderRadius: 5, padding: '6px 10px', color: '#e0e0e0', fontSize: 13 }} />
        </div>
        {/* Yield Max */}
        <div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 5 }}>Yield Max</div>
          <input type="number" step="0.5" min="0" max="30" value={(filters.yieldMax * 100).toFixed(1)}
            onChange={e => onChange({ ...filters, yieldMax: parseFloat(e.target.value) / 100 || 0.15 })}
            style={{ width: '100%', background: '#1a1a1a', border: '1px solid #222', borderRadius: 5, padding: '6px 10px', color: '#e0e0e0', fontSize: 13 }} />
        </div>
        {/* Type */}
        <div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 5 }}>Type</div>
          <select value={filters.isEtf} onChange={e => onChange({ ...filters, isEtf: e.target.value })}
            style={{ width: '100%', background: '#1a1a1a', border: '1px solid #222', borderRadius: 5, padding: '6px 10px', color: '#e0e0e0', fontSize: 13 }}>
            <option value="both">Stocks + ETFs</option>
            <option value="false">Stocks only</option>
            <option value="true">ETFs only</option>
          </select>
        </div>
        {/* Sector */}
        <div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 5 }}>Sector</div>
          <select value={filters.sector} onChange={e => onChange({ ...filters, sector: e.target.value })}
            style={{ width: '100%', background: '#1a1a1a', border: '1px solid #222', borderRadius: 5, padding: '6px 10px', color: '#e0e0e0', fontSize: 13 }}>
            {sectors.map(s => <option key={s} value={s}>{s || 'All Sectors'}</option>)}
          </select>
        </div>
        {/* Mkt Cap Min */}
        <div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 5 }}>Min Mkt Cap</div>
          <select value={filters.marketCapMin} onChange={e => onChange({ ...filters, marketCapMin: parseInt(e.target.value) })}
            style={{ width: '100%', background: '#1a1a1a', border: '1px solid #222', borderRadius: 5, padding: '6px 10px', color: '#e0e0e0', fontSize: 13 }}>
            <option value="500000000">$500M+</option>
            <option value="1000000000">$1B+</option>
            <option value="5000000000">$5B+</option>
            <option value="10000000000">$10B+</option>
          </select>
        </div>
        {/* Payout Max */}
        <div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 5 }}>Max Payout</div>
          <select value={filters.payoutMax} onChange={e => onChange({ ...filters, payoutMax: parseFloat(e.target.value) })}
            style={{ width: '100%', background: '#1a1a1a', border: '1px solid #222', borderRadius: 5, padding: '6px 10px', color: '#e0e0e0', fontSize: 13 }}>
            <option value="0.75">≤ 75%</option>
            <option value="0.90">≤ 90%</option>
            <option value="1.00">≤ 100%</option>
            <option value="2.00">Any</option>
          </select>
        </div>
      </div>
      <button onClick={onRun} disabled={loading}
        style={{ background: loading ? '#1a2a2a' : '#00d4aa', color: loading ? '#555' : '#0a0a0a', border: 'none', borderRadius: 6, padding: '9px 22px', fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: 0.3 }}>
        {loading ? 'Scanning...' : 'Run Screen'}
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ComparableScreener({ session, userTier, portfolioTickers = [], onUpgrade }) {
  const [activeTab, setActiveTab] = useState('alternatives');
  const [selectedTicker, setSelectedTicker] = useState(portfolioTickers[0] || '');
  const [altData, setAltData] = useState(null);
  const [screenData, setScreenData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ yieldMin: 0.03, yieldMax: 0.15, sector: '', isEtf: 'both', marketCapMin: 1000000000, payoutMax: 1.0 });
  const [assetClassFilter, setAssetClassFilter] = useState('all');

  const token = session?.access_token;

  useEffect(() => {
    if (activeTab === 'alternatives' && selectedTicker) runAlternatives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicker]);

  async function runAlternatives(force = false) {
    if (!selectedTicker) return;
    setLoading(true);
    setError(null);
    setAltData(null);
    try {
      const url = `${WORKER_URL}/api/screener/alternatives?ticker=${selectedTicker}${force ? '&force=true' : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      setAltData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function runScreen(force = false) {
    setLoading(true);
    setError(null);
    setScreenData(null);
    try {
      const params = new URLSearchParams({
        yield_min:      String(filters.yieldMin),
        yield_max:      String(filters.yieldMax),
        sector:         filters.sector,
        is_etf:         filters.isEtf,
        market_cap_min: String(filters.marketCapMin),
        payout_max:     String(filters.payoutMax),
        asset_class:    assetClassFilter
      });
      if (force) params.set('force', 'true');
      const res = await fetch(`${WORKER_URL}/api/screener/screen?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      setScreenData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const modeBSeed = { ticker: '—', isEtf: false };

  const tabStyle = (t) => ({
    padding: '8px 18px',
    color: activeTab === t ? '#00d4aa' : '#555',
    fontWeight: activeTab === t ? 600 : 400,
    fontSize: 13,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === t ? '2px solid #00d4aa' : '2px solid transparent',
    transition: 'color 0.15s'
  });

  // Client-side asset class filter for Mode A results
  const filteredAltResults = altData?.results ? altData.results.filter(r => {
    if (assetClassFilter === 'all') return true;
    const ac = (r.assetClass || '').toLowerCase();
    const FIXED_KW = ['fixed income', 'bond', 'treasury', 'corporate bond', 'municipal', 'muni', 'debt', 'credit', 'government bond', 'high yield bond', 'inflation protected'];
    const isFI = FIXED_KW.some(kw => ac.includes(kw));
    if (assetClassFilter === 'equity' && r.isEtf && isFI) return false;
    if (assetClassFilter === 'fixed_income' && r.isEtf && !isFI) return false;
    if (assetClassFilter === 'stocks_only' && r.isEtf) return false;
    if (assetClassFilter === 'etfs_only' && !r.isEtf) return false;
    return true;
  }) : [];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 40px' }}>
      {/* Page header */}
      <div style={{ marginBottom: 24, paddingTop: 8 }}>
        <h1 style={{ color: '#fff', fontWeight: 700, fontSize: 22, margin: 0 }}>Comparable Screener</h1>
        <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Find dividend alternatives ranked by income quality, business health, and market metrics.</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #1a1a1a', marginBottom: 24 }}>
        <button style={tabStyle('alternatives')} onClick={() => setActiveTab('alternatives')}>Find Alternatives</button>
        <button style={tabStyle('screen')} onClick={() => setActiveTab('screen')}>Screen Universe</button>
      </div>

      {/* ── Mode A: Find Alternatives ── */}
      {activeTab === 'alternatives' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: '#555', marginBottom: 5 }}>Your holding</div>
              <select value={selectedTicker} onChange={e => setSelectedTicker(e.target.value)}
                style={{ width: '100%', background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: 6, padding: '10px 14px', color: '#e0e0e0', fontSize: 14 }}>
                {portfolioTickers.length === 0 && <option value="">No holdings in portfolio</option>}
                {portfolioTickers.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ paddingTop: 18 }}>
              <button onClick={() => runAlternatives(false)} disabled={loading || !selectedTicker}
                style={{ background: loading ? '#1a2a2a' : '#00d4aa', color: loading ? '#555' : '#0a0a0a', border: 'none', borderRadius: 6, padding: '10px 22px', fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Analyzing...' : 'Run Analysis'}
              </button>
            </div>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: 48, color: '#555' }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>Scanning universe & enriching financials…</div>
              <div style={{ fontSize: 11, color: '#333' }}>Revenue, FCF, coverage, short interest — this takes ~8s</div>
            </div>
          )}

          {error && !loading && (
            <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid #ef444430', borderRadius: 8, padding: '12px 16px', color: '#ef4444', fontSize: 13, marginBottom: 20 }}>
              {error}
            </div>
          )}

          {altData && !loading && (
            <>
              <span style={{ fontSize: 11, color: '#333', marginBottom: 12, display: 'block', textAlign: 'right' }}>
                Cached result · <button onClick={() => runAlternatives(true)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' }}>refresh</button>
              </span>

              <SeedCard seed={altData.seed} />

              {altData.message && (
                <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: 32 }}>{altData.message}</div>
              )}

              {altData.results && altData.results.length > 0 && (
                <>
                  <AssetClassTabs value={assetClassFilter} onChange={setAssetClassFilter} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <BarChart3 size={14} color="#555" />
                    <span style={{ fontSize: 12, color: '#555', letterSpacing: 0.5 }}>
                      {filteredAltResults.length} ALTERNATIVES RANKED BY INCOME QUALITY SCORE
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
                    {filteredAltResults.map((r, i) => (
                      <ResultCard key={r.symbol} result={r} seed={altData.seed} rank={i + 1} />
                    ))}
                  </div>
                  {filteredAltResults.length === 0 && (
                    <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: 32 }}>No results match this filter. Try a different asset class.</div>
                  )}
                </>
              )}

              {altData.results && altData.results.length === 0 && !altData.message && (
                <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: 32 }}>No alternatives found in this yield band. Try a different holding.</div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Mode B: Screen Universe ── */}
      {activeTab === 'screen' && (
        <div>
          <AssetClassTabs value={assetClassFilter} onChange={v => { setAssetClassFilter(v); }} />
          <FilterBar filters={filters} onChange={setFilters} onRun={runScreen} loading={loading} />

          {loading && (
            <div style={{ textAlign: 'center', padding: 48, color: '#555' }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>Scanning universe & enriching financials…</div>
              <div style={{ fontSize: 11, color: '#333' }}>Revenue, FCF, coverage, short interest — this takes ~15s for large screens</div>
            </div>
          )}

          {error && !loading && (
            <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid #ef444430', borderRadius: 8, padding: '12px 16px', color: '#ef4444', fontSize: 13, marginBottom: 20 }}>
              {error}
            </div>
          )}

          {screenData && !loading && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <BarChart3 size={14} color="#555" />
                <span style={{ fontSize: 12, color: '#555' }}>{screenData.results?.length || 0} results · {screenData.total || 0} matched filters · ranked by Income Quality Score</span>
                {screenData.fromCache && <span style={{ fontSize: 11, color: '#333', marginLeft: 'auto' }}>cached · <button onClick={() => runScreen(true)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' }}>refresh</button></span>}
              </div>
              {screenData.results?.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
                  {screenData.results.map((r, i) => (
                    <ResultCard key={r.symbol} result={r} seed={modeBSeed} rank={i + 1} />
                  ))}
                </div>
              ) : (
                <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: 32 }}>No results matched your filters. Try widening the yield range or market cap.</div>
              )}
            </>
          )}

          {!screenData && !loading && !error && (
            <div style={{ textAlign: 'center', padding: 48, color: '#333', fontSize: 13 }}>
              Set your filters above and run the screen to see results.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
